/**
 * Defines the type of DRM system used for a media stream.
 *
 * @privateRemarks
 * TODO: rename to DrmSystem because the values aren't exactly about encryption.
 */
export const EncryptionType = {
  /**
   * Microsoft PlayReady DRM system
   *
   * @remarks
   * PlayReady is a DRM system from Microsoft that is used to encrypt media streams.
   *
   * PlayReady is only available on Windows and Xbox devices in combination with the Edge browser,
   * as well some Smart TVs and set-top boxes.
   *
   * We recommend to use PlayReady on the following devices:
   * - Xbox devices
   * - Sky Q devices
   * - Magenta devices
   * - Vodafone devices
   *
   * @see https://www.microsoft.com/playready/documents/
   */
  PlayReady: 'playready',

  /**
   * Google Widevine DRM system
   *
   * @remarks
   * Widevine is a DRM system from Google that is used to encrypt media streams.
   *
   * Widevine is available on most Android devices, as well as most Smart TVs and set-top boxes.
   * In general if available, we recommend to use Widevine, unless the device has known issues with it or performs better with PlayReady.
   *
   * We recommend to use Widevine on the following devices:
   * - Desktop Chrome browser
   * - Android devices
   * - Tizen devices
   * - WebOS devices
   * - PlayStation devices
   * - Sky XiOne devices
   * - Sky Stream devices
   *
   * @see https://www.widevine.com/
   */
  Widevine: 'widevine',

  /**
   * Apple FairPlay DRM system
   *
   * @remarks
   *
   * FairPlay is a DRM system from Apple that is used to encrypt media streams.
   *
   * FairPlay is only available on iOS and macOS devices in combination with the Safari browser.
   * In general if available, we recommend to use FairPlay.
   *
   * Beware that we support FairPlay **only** in conjunction with HLS streams.
   *
   * We recommend to use FairPlay on the following devices:
   * - iOS devices
   * - macOS devices
   *
   * @see https://developer.apple.com/streaming/fps/
   */
  FairPlay: 'fairplay',

  /**
   * No DRM system
   *
   * @remarks
   * This is a special value that can be used to indicate a progressive or unencrypted media stream.
   *
   * You do not have to specify this value explicitly, as it is the default value if no encryption type is specified.
   */
  None: 'none',
} as const;

/**
 * {@inheritDoc EncryptionType:var}
 */
export type EncryptionType = (typeof EncryptionType)[keyof typeof EncryptionType];
